// React
import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

// Hooks and Redux
import { useAppSelector } from "../../../redux/hooks"
import useDebounce from "../../../hooks/useDebounce"

// MUI Components
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import FormControl from "@mui/material/FormControl"
import Grid from "@mui/material/Grid"
import Icon from "@mui/material/Icon"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Tooltip from "@mui/material/Tooltip"

import {
  GridCellParams,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  DataGrid,
  enUS,
} from "@mui/x-data-grid"
// Utils
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"

// Types
import theme from "../../../styles/theme"
import { thTHGrid } from "../../../@types/DataGridLocaleText"
import { CompanyInter } from "../../../@types"
import RoleType from "../../../@types/RoleType"

// Services
import { getAllExcelECPagingData } from "../../../services/ECService"
import { getAllActiveWorkSchedule } from "../../../services/MasterDataService"

//pop-up
import PopupChangeStatusEC from "./PopupChangeStatusEC"

const DataToECPagingSupport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { rolesite } = useAppSelector((state) => state.user)
  const userRoleType = useAppSelector((state) => state.user.roletype)

  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  })
  const [totalRows, setTotalRows] = useState<number>(0)
  const { sites: sitesData, companies: companyList } = useAppSelector(
    (state) => state.masterData
  )
  const [workScheduleList, setWorkScheduleList] = useState<
    { external_code: string; label: string }[]
  >([])
  const [clickedId, setClickedId] = useState<string>("")
  const [editClicked, setEditClicked] = useState<boolean>(false)
  const [editId, setEditId] = useState<string>("")
  const [filterDataSelect, setFilterDataSelect] = useState<any[]>([])

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  useEffect(() => {
    const fetchWorkSchedule = async () => {
      try {
        const response = await getAllActiveWorkSchedule("Active")
        if (response.data?.success)
          setWorkScheduleList(response?.data?.workSchedules)
      } catch {
        navigate("/servererror", { replace: true })
      }
    }
    fetchWorkSchedule()
  }, [navigate])

  useEffect(() => {
    fetchData(paginationModel.page, paginationModel.pageSize)
  }, [paginationModel.page, paginationModel.pageSize, debouncedSearchQuery])

  useEffect(() => {
    if (searchQuery !== "") setIsSearching(true)
  }, [searchQuery])

  const updateActiveEditEC = async (status: boolean, id: string) => {
    if (id.length > 0 && status === false) {
      // UpdateStatusActiveEC(id, status)
      setEditId(id)
    } else {
      // UpdateStatusActiveEC(editId, status)
      setEditId("")
    }
  }

  const fetchData = async (page: number, pageSize: number) => {
    try {
      const response = await getAllExcelECPagingData({
        site_ids: rolesite ?? [],
        job_ids: [],
        ec_ids: [],
        page: page + 1,
        pageSize,
        query: debouncedSearchQuery,
      } as any)
      if (response.data?.success) {
        setRows(response?.data?.ECData?.data ?? [])
        setTotalRows(response?.data?.ECData?.total ?? 0)
      }
    } catch {
      navigate("/servererror", { replace: true })
    } finally {
      setTimeout(() => {
        setLoading(false)
        setIsSearching(false)
      }, 500)
    }
  }

  function mapNameCompany(companyCode: string) {
    const data: CompanyInter = companyList.find(
      (item: CompanyInter) => companyCode === item.company_code
    )!
    return data ? `${data.company_code} - ${data.company_name}` : companyCode
  }

  const mapSites = (site_id: string) => {
    const data: any = sitesData.find((item: any) => site_id === item.site_id)!
    return data ? `${data.site_name}[${data.site_id}]` : site_id
  }

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </GridToolbarContainer>
    )
  }

  const columns: GridColDef[] = [
    {
      field: "edit",
      headerName: `${t("Edit")}`,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      pinnable: true,
      width: userRoleType === RoleType.Super_Admin ? 60 : 0,
      renderCell: (params) => {
        if (
          userRoleType === RoleType.Super_Admin 
        ) {
          return (
            <Tooltip title={t("Edit")}>
              <Icon
                onClick={() => {
                  setClickedId(params.row.id)
                  setEditClicked(true)
                  updateActiveEditEC(false, params.row.id)
                  setFilterDataSelect(params.row)
                }}
                sx={{ cursor: "pointer", color: theme.palette.button.main }}
              >
                edit
              </Icon>
            </Tooltip>
          )
        }
      },
    },
    {
      field: "is_active",
      headerName: `${t("Active")}`,
      headerAlign: "center",
      width: 120,
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) =>
        params.row.is_active === true ? (
          <Chip
            variant="outlined"
            color="success"
            label={t("Active")}
            size="small"
          />
        ) : (
          <Chip
            variant="outlined"
            color="error"
            label={t("Not Active")}
            size="small"
          />
        ),
    },
    {
      field: "exported",
      headerName: `${t("Exported")}`,
      headerAlign: "center",
      width: 120,
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) =>
        params.row.exported === true ? (
          <Chip
            variant="outlined"
            color="success"
            label={t("Exported")}
            size="small"
          />
        ) : (
          <Chip
            variant="outlined"
            color="warning"
            label={t("Not Exported")}
            size="small"
          />
        ),
    },
    {
      field: "exported_date",
      headerName: `${t("Exported Date")}`,
      width: 150,
      valueGetter: (params: GridCellParams) =>
        Date.parse(params.row.exported_date),
      renderCell: (params: GridCellParams) =>
        typeof params.row.exported_date === "string"
          ? dayjs(params.row.exported_date).format("DD/MM/YYYY HH:mm")
          : "-",
    },
    {
      field: "first_name_th",
      headerName: `${t("First name (Thai language)")}`,
      width: 150,
      valueGetter: (params: GridCellParams) => params.row.first_name_th,
      renderCell: (params: GridCellParams) => params.row.first_name_th ?? "-",
    },
    {
      field: "last_name_th",
      headerName: `${t("Last name (Thai language)")}`,
      width: 200,
      valueGetter: (params: GridCellParams) => params.row.last_name_th,
      renderCell: (params: GridCellParams) => params.row.last_name_th ?? "-",
    },
    {
      field: "created_by",
      headerName: `${t("Created By")}`,
      width: 150,
      valueGetter: (params: GridCellParams) => params.row.created_by,
      renderCell: (params: GridCellParams) => params.row.created_by ?? "-",
    },
    {
      field: "created_date",
      headerName: `${t("Created Date")}`,
      width: 150,
      renderCell: (params: GridCellParams) => {
        let created_date = dayjs(params.row.created_date).format(
          i18n.language === "en" ? "DD/MM/YYYY HH:mm" : "DD/MM/BBBB HH:mm"
        )

        return <Typography sx={{ fontSize: 14 }}>{created_date}</Typography>
      },
    },
    {
      field: "modified_by",
      headerName: `${t("Modified By")}`,
      width: 200,
      valueGetter: (params: GridCellParams) => params.row.modified_by,
      renderCell: (params: GridCellParams) => params.row.modified_by ?? "-",
    },
    {
      field: "modified_date",
      headerName: `${t("Modified Date")}`,
      width: 150,
      renderCell: (params: GridCellParams) => {
        let modified_date = params.row.modified_date
          ? dayjs(params.row.modified_date).format(
              i18n.language === "en" ? "DD/MM/YYYY HH:mm" : "DD/MM/BBBB HH:mm"
            )
          : "-"

        return <Typography sx={{ fontSize: 14 }}>{modified_date}</Typography>
      },
    },
  ]

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "70px",
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          {t("Data to EC")}
        </Typography>
      </Box>

      <Box sx={{ paddingX: 3, mt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl sx={{ width: "100%" }}>
              <TextField
                label={t("Search")}
                placeholder={t("Search by name, national ID, position...")!}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery.length > 0 && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => setSearchQuery("")}
                      >
                        <Icon>clear</Icon>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ width: "100%", paddingX: 3, mt: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" gap={2}>
            <CircularProgress color="success" size={20} />
            <Typography align="center">{t("Loading")}...</Typography>
          </Box>
        ) : isSearching ? (
          <Typography align="center">{t("Searching")}...</Typography>
        ) : rows.length !== 0 ? (
          <Box display="inline-grid" sx={{ width: "100%" }}>
            <DataGrid
              getRowId={(row) => row.id}
              rows={rows}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pagination
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[10, 20, 30, 40, 50]}
              getRowHeight={() => 50}
              disableRowSelectionOnClick
              sx={{
                "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus":
                  {
                    outline: "none !important",
                  },
                fontSize: 13,
              }}
              slots={{ toolbar: CustomToolbar }}
              localeText={
                i18n.language === "en"
                  ? enUS.components.MuiDataGrid.defaultProps.localeText
                  : thTHGrid
              }
            />
          </Box>
        ) : (
          <Typography align="center">{t("No data")}</Typography>
        )}
        <Box sx={{ width: "100%", marginTop: 3, paddingX: 3 }}>
          <PopupChangeStatusEC
            open={editClicked}
            keepMounted
            onClose={() => {
              setEditClicked(false)
            }}
            data={filterDataSelect}
            ec_Ids={editId}
            typeEC="dataToEC"
          />
        </Box>
      </Box>
    </>
  )
}

export default DataToECPagingSupport
